/**
 * 글의 정규 공개 경로 — 플랫폼 전체가 쓰는 **하나의** 계산기 (ADR-0105 개정).
 *
 * 우선순위: 콘텐츠 모델 presentation(fixed_page → detail → category_tree) →
 * 호환 컬렉션(`resolvePostPath`) → 레거시 `/blog/{slug}`. `data_only` 모델은
 * 상세 주소가 없으므로 호환 컬렉션이 있으면 그 규칙을, 없으면 null 을 돌려준다.
 *
 * 이 값은 저장 시 `posts.public_path` 로 물질화되고(경로 이력 포함), 웹훅·사이트맵·
 * 단축링크·내부 링크 키·리다이렉트는 저장된 값을 읽는다. 여기 말고 다른 곳에서
 * 주소를 다시 계산하지 않는다.
 */
import {
  resolveCategoryTreeEntryPath,
  type CategoryTreeTerm,
} from "./category-tree";
import {
  resolvePostCollection,
  resolvePostPath,
  type RouteCollection,
} from "./collections";

/** 콘텐츠 모델 presentation 의 구조적 최소형(cms-models 스키마와 호환). */
export type PublicPathPresentation =
  | { readonly kind: "data_only" }
  | { readonly kind: "fixed_page"; readonly path: string }
  | { readonly kind: "detail"; readonly detailPath: string }
  | {
      readonly kind: "category_tree";
      readonly basePath: string;
      readonly categoryDepth: number;
    };

export interface PublicPathPost {
  readonly slug: string;
  readonly collectionKey?: string | null;
  readonly modelKey?: string | null;
  readonly terms: ReadonlyArray<{
    readonly id: string;
    readonly taxonomy: string;
    readonly slug: string;
  }>;
}

export interface PublicPathContext {
  /** 글의 모델 presentation. 모델 글이 아니거나 모델을 못 찾으면 null. */
  readonly presentation: PublicPathPresentation | null;
  /** 모델의 호환 컬렉션 key(`compatibilityCollectionKey`, 미지정이면 모델 key). */
  readonly compatibilityCollectionKey?: string | null;
  readonly collections: readonly RouteCollection[];
  readonly categories: readonly CategoryTreeTerm[];
}

export const LEGACY_BLOG_DETAIL_PATH = "/blog";

/** 글의 정규 공개 경로(origin 제외). 상세 주소가 없으면 null. */
export function resolvePostPublicPath(
  post: PublicPathPost,
  context: PublicPathContext,
): string | null {
  const presentation = context.presentation;
  if (presentation && presentation.kind !== "data_only") {
    if (presentation.kind === "fixed_page") return presentation.path;
    if (presentation.kind === "detail") {
      if (!presentation.detailPath.includes(":slug")) return null;
      return presentation.detailPath.replace(":slug", encodeURIComponent(post.slug));
    }
    return resolveCategoryTreeEntryPath(presentation, post, context.categories);
  }

  const collectionKey =
    post.collectionKey ??
    (presentation ? context.compatibilityCollectionKey ?? post.modelKey ?? null : null);
  const routable = { slug: post.slug, terms: post.terms, collectionKey };
  if (!resolvePostCollection(routable, context.collections)) {
    // 모델 글인데 규칙도 컬렉션도 없으면 주소가 없다. 레거시 단일 블로그만 /blog 폴백.
    if (presentation) return null;
    return `${LEGACY_BLOG_DETAIL_PATH}/${post.slug}`;
  }
  return resolvePostPath(routable, context.collections);
}
